import React from 'react';
import Collapsible from 'react-collapsible';
import classnames from 'classnames';
import { useGuideConfig, useNeuroMessages, NeuroMessage } from '@ignitesales/neuro-application';
import { FormattedHTMLMessage } from 'react-intl';
import ComparisonTable from './ComparisonTable';
import FeaturesTable from './FeaturesTable';

const productMap = new Map([
  ['checking', 'open'],
  ['savings', 'open'],
  ['credit-cards', 'open'],
  ['savings-specialty', 'schedule'],
  ['lending', 'apply'],
  ['insurance', 'schedule'],
  [22031, 'open'],
  [22055, 'null'],
  [22020, 'open'],
  [22024, 'open'],
]);

const productActionMap = new Map([
  ['open', 'productApplyClicked'],
  ['apply', 'productApplyClicked'],
  ['schedule', 'locateBranchClicked'],
  ['contact', 'contactMeClicked'],
]);

const FeatureMessage = ({ id, values }) => {
  const { getMessage } = useNeuroMessages();
  const message = getMessage(id);

  // Return early if message is undefined
  if (!message) {
    return null;
  }

  // Split message into parts and render each part appropriately
  const parts = message.split(/(<feature_[^>]+>|<[^>]+>)/g);

  const renderPart = (part, index) => {
    // Handle feature tags
    const featureMatch = part.match(/<feature_([^>]+)>/);
    if (featureMatch) {
      const Component = values[`feature_${featureMatch[1]}`];
      if (Component) {
        // Find the closing tag
        const closingTag = `</feature_${featureMatch[1]}>`;
        const closingIndex = parts.findIndex((p, i) =>
          i > index && p === closingTag
        );

        if (closingIndex !== -1) {
          // Get all content between opening and closing tags
          const content = parts.slice(index + 1, closingIndex).join('');
          // If the component is a function that expects chunks, call it with content
          if (typeof Component === 'function') {
            return Component(content);
          }
          // Otherwise render it as a React component with children
          return <Component key={`feature-${index}`}>{content}</Component>;
        }
        // If no closing tag found, render as self-closing
        if (typeof Component === 'function') {
          return Component('');
        }
        return <Component key={`feature-${index}`} />;
      }
      return part;
    }

    // Handle HTML tags
    const htmlMatch = part.match(/<([^>]+)>/);
    if (htmlMatch) {
      const tag = htmlMatch[1].split(' ')[0];
      const Component = values[tag];
      if (Component) {
        // Find the closing tag
        const closingTag = `</${tag}>`;
        const closingIndex = parts.findIndex((p, i) =>
          i > index && p === closingTag
        );

        if (closingIndex !== -1) {
          // Get all content between opening and closing tags
          const content = parts.slice(index + 1, closingIndex).join('');
          // If the component is a function that expects chunks, call it with content
          if (typeof Component === 'function') {
            return Component(content);
          }
          // Otherwise render it as a React component with children
          return <Component key={`html-${index}`}>{content}</Component>;
        }
        // If no closing tag found, render as self-closing
        if (typeof Component === 'function') {
          return Component('');
        }
        return <Component key={`html-${index}`} />;
      }
      // If no component found, return the tag as text
      return part;
    }

    // Return text as is
    return part;
  };

  // Process the parts and build the content tree
  const buildContentTree = (parts, values, startIndex = 0) => {
    const elements = [];
    let currentIndex = startIndex;

    while (currentIndex < parts.length) {
      const part = parts[currentIndex];

      // Handle self-closing tags like <br />
      if (part.match(/<br\s*\/>/)) {
        elements.push(values.br);
        currentIndex++;
        continue;
      }

      // Skip closing tags as they're handled by their opening tags
      if (part.startsWith('</')) {
        currentIndex++;
        continue;
      }

      // Handle opening tags
      if (part.startsWith('<')) {
        const tag = part.match(/<([^>]+)>/)[1].split(' ')[0];
        const Component = values[tag];

        if (Component) {
          // Find the closing tag
          const closingTag = `</${tag}>`;
          const closingIndex = parts.findIndex((p, i) =>
            i > currentIndex && p === closingTag
          );

          if (closingIndex !== -1) {
            // Get content between opening and closing tags
            const content = parts.slice(currentIndex + 1, closingIndex).join('');
            // Process the content to handle nested tags
            const processedContent = buildContentTree(
              content.split(/(<feature_[^>]+>|<[^>]+>)/g),
              values
            );

            if (typeof Component === 'function') {
              elements.push(Component(processedContent));
            } else {
              elements.push(
                <Component key={`${tag}-${currentIndex}`}>
                  {processedContent}
                </Component>
              );
            }
            currentIndex = closingIndex + 1;
            continue;
          }
        }
      }

      // Handle text content
      if (!part.startsWith('<')) {
        elements.push(part);
      }

      currentIndex++;
    }

    return elements;
  };

  return <>{buildContentTree(parts, values)}</>;
};

const balanceRule = "(checkingBalance >= 60) OR (savingsBalance >= 70) OR (checkingBalance == 50 AND savingsBalance >= 60) AND (checkingBalance + savingsBalance >= 5000)";

const Product = ({ leadInfo, product, productActionCompleted }) => {
  const { getMessage } = useNeuroMessages();
  const { isBranchGuide } = useGuideConfig();
  const cssBEM = 'product';
  const classes = classnames(cssBEM);
  const ctaType = React.useMemo(
    () =>
      productMap.get(parseInt(product.id)) || productMap.get(product.groupType),
    [product]
  );
  const ctaAction = React.useMemo(() => productActionMap.get(ctaType), [
    product,
  ]);
  const learnMoreUrl = getMessage(`${ product.id }.url`);
  const ctaUrl = getMessage(`${ product.id }.cta.1`);
  const learnMoreClicked = () =>
    productActionCompleted('productDetailsClicked', product.id, 'learnMore', true);

  const onCTAClick = (e, isSecondary) => {
    productActionCompleted(ctaAction, product.id, 'completed', true);
    if (!isSecondary && ctaAction && ctaType !== 'contact') {
      if (!leadInfo.sent) {
        leadInfo.openContactForm();
      } else {
        window.open(ctaUrl, '_blank', 'noopener');
      }
    } else {
      if (!leadInfo.sent) {
        leadInfo.openContactForm();
      } else {
        leadInfo.openContactConfirmationModal();
      }
    }
  };

  const actionUrl = (p, chunks) => (
    <a href={p.url} className={p.class} target="_blank" rel="noopener">
      {chunks}
    </a>
  );

  const htmlValues = {
    br: <br />,
    b: (...chunks) => <b>{chunks}</b>,
    p: (...chunks) => <p>{chunks}</p>,
    strong: (...chunks) => <strong>{chunks}</strong>,
    sup: (...chunks) => <sup>{chunks}</sup>,
    ul: (...chunks) => <ul>{chunks}</ul>,
    ol: (...chunks) => <ol>{chunks}</ol>,
    u: (...chunks) => <u>{chunks}</u>,
    li: (...chunks) => <li>{chunks}</li>,
    em: (...chunks) => <em>{chunks}</em>,

    feature_allpointAndFreedomAlliance: (...chunks) =>
      actionUrl(
        {
          url: 'https://www.fcbanking.com/branch-locations/',
          class: 'inlineUrl',
        },
        chunks
      ),
    feature_rates: (...chunks) =>
      actionUrl(
        {
          url: 'https://www.fcbanking.com/resources/rates/',
          class: 'inlineUrl',
        },
        chunks
      ),
    feature_checking: (...chunks) =>
      actionUrl(
        {
          url: 'https://www.fcbanking.com/personal/checking-accounts/',
          class: 'inlineUrl',
        },
        chunks
      ),
    feature_overdraft: (...chunks) =>
      actionUrl(
        {
          url: 'https://www.fcbanking.com/personal/checking-accounts/overdraft-information/',
          class: 'inlineUrl',
        },
        chunks
      ),
    feature_locations: (...chunks) =>
      actionUrl(
        {
          url: 'https://www.fcbanking.com/branch-locations/',
          class: 'inlineUrl',
        },
        chunks
      ),
    feature_2_22073: (...chunks) =>
      actionUrl(
        {
          url:
            'https://www.fcbanking.com/personal/checking-accounts/debit-cards/contactless-cards-faqs/',
        },
        chunks
      ),
    features_5_22020: (...chunks) =>
      actionUrl(
        {
          url:
            'https://www.fcbanking.com/resources/building-financial-confidence/',
        },
        chunks
      ),
    features_3_22093: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/referafriend',
        },
        chunks
      ),
    disclosures_1_22093: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/referafriend',
        },
        chunks
      ),
    description_1_22093: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/referafriend',
          class:
            'inlineUrl',
        },
        chunks
      ),
    description_1_22056: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/referafriend',
          class:
            'inlineUrl',
        },
        chunks
      ),
    features_2_22056: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/branch-locations/',
          class:
            'inlineUrl',
        },
        chunks
      ),
    features_3_22056: (...chunks) =>
      actionUrl(
        {
          url:
            'http://www.fcbanking.com/personal/checking-accounts',
          class:
            'inlineUrl',
        },
        chunks
      ),
  };

  const isProductWithComparisonTable = () => getMessage(`${ product.id }.features.table.count`) > 0;
  const isProductWithFeatureTable = () => getMessage(`${ product.id }.features.column.name`) === 'Feature';

  const renderComparisonTables = () => {
    const tableCount = getMessage(`${ product.id }.features.table.count`);
    const tables = Array(tableCount)
      .fill(1)
      .map((v, index) => {
        const msgIndex = index + 1;
        const products = getMessage(`${ product.id }.features.table.${ msgIndex }`);
        return (
          <div className="comparison-table--container">
            <ComparisonTable
              products={products}
            />
          </div>
        )
      });
    return tables;
  }

  const renderFeatureTable = () => {
    const products = getMessage(`${ product.id }.features.columns`);

    return (
      <div className="features-table--container">
        <FeaturesTable
          products={products}
          htmlValues={htmlValues}
        />
      </div>
    )
  }

  const renderProductFeatures = () => {
    const count = getMessage(`${ product.id }.features.count`);
    const features = Array(count)
      .fill(1)
      .map((v, index) => {
        const msgIndex = index + 1;
        const id = `${ product.id }.features.${ msgIndex }`;
        return <li key={id}><FeatureMessage id={id} values={htmlValues} /></li>;
      });

    return (
      features.length > 0 && (
        <>
          <div className={`${ cssBEM }__features`}>
            <Collapsible
              classParentString="collapsible"
              className={'product__collapsible'}
              openedClassName={'product__collapsible--addOns'}
              contentInnerClassName="collapsible__content"
              trigger={
                <>
                  <NeuroMessage id={`product.featureHeader`} />
                  <i className="fa fa-angle-right" aria-hidden="true"></i>
                </>
              }
              triggerTagName="button"
            >
              {isProductWithComparisonTable() ? renderComparisonTables() : isProductWithFeatureTable() ? renderFeatureTable() : <ul className={`${ cssBEM }__features-list`}>{features}</ul>}
            </Collapsible>
          </div>
          <div className={`${ cssBEM }__features--printOnly`}>
            <span className='collapsible__trigger'>
              <NeuroMessage id={`product.featureHeader`} />
            </span>
            <ul className={`${ cssBEM }__features-list`}>{features}</ul>
          </div>
        </>
      )
    );
  };

  const renderProductDisclosures = () => {
    const count = getMessage(`${product.id}.disclosures.count`);
    if (!count) return null;

    const disclosures = Array(count)
      .fill(1)
      .map((v, index) => {
        const msgIndex = index + 1;
        const id = `${product.id}.disclosures.${msgIndex}`;
        return <li key={id}><FeatureMessage id={id} values={htmlValues} /></li>;
      });

    return (
      disclosures.length > 0 && (
        <div className={`${cssBEM}__disclosures`}>
          <Collapsible
            classParentString="collapsible"
            className={'product__collapsible product__collapsible--disclosures'}
            openedClassName={'product__collapsible--addOns'}
            contentInnerClassName="collapsible__content"
            trigger={
              <>
                <NeuroMessage id={`products.disclosures`} />
                <i className="fa fa-angle-right" aria-hidden="true"></i>
              </>
            }
            triggerTagName="button"
          >
            <ul className={`${cssBEM}__disclosures-list`}>{disclosures}</ul>
          </Collapsible>
        </div>
      )
    );
  };

  const renderInlineDisclosure = () => {
    return (
      <div className={`${ cssBEM }__inlineDisclosure`}>
        <NeuroMessage id={`${ product.id }.inlineDisclosure`} values={htmlValues} />
      </div>
    );
  }

  return (
    <div className={classes} key={product.id} data-product-id={product.id}>
      <div className={`${ cssBEM }__container`}>
        <div className={`${ cssBEM }__sub-container`}>
          <div className={`${ cssBEM }__header`}>
            <NeuroMessage id={`${ product.id }.url`}>
              {(url) => (
                <h2 className={`${ cssBEM }__url`} onClick={learnMoreClicked}>
                  <a href={url} target="_blank" rel="noopener">
                    <NeuroMessage
                      id={`${ product.id }.name`}
                      values={htmlValues}
                    />
                  </a>
                </h2>
              )}
            </NeuroMessage>
          </div>

          <div className={`${ cssBEM }__details`}>
            <div className={`${ cssBEM }__description`}>
              <span>
                <FeatureMessage
                  id={`${ product.id }.description.abbreviated`}
                  values={htmlValues}
                />
              </span>
            </div>

            {renderProductFeatures()}
            {learnMoreUrl && (
              <a
                href={learnMoreUrl}
                onClick={learnMoreClicked}
                target="_blank"
                aria-label={`Learn more about ${product.name}`}
                className={`${cssBEM}__learnMore`}
              >
                <NeuroMessage id="product.learnMore" />
              </a>
            )}
            {renderProductDisclosures()}
            {renderInlineDisclosure()}
          </div>
        </div>

        {!isBranchGuide && ctaAction && (
          <div className={`${ cssBEM }__cta`}>
            {ctaType !== 'contact' &&
              <button className="btn btn--cta" onClick={onCTAClick}>
                <NeuroMessage
                  id={`product.cta.${ ctaType }`}
                  values={{ name: product.name }}
                />
              </button>
            }
          </div>
        )}
      </div>
    </div>
  );
};

export default Product;
